import { initializeBindings } from "https://static.cloud.coveo.com/atomic/v3/index.esm.js";
import {
    buildBreadcrumbManager,
    buildQuerySummary,
} from "https://static.cloud.coveo.com/headless/v3/headless.esm.js";

class CustomFacetFilters extends HTMLElement {

    async connectedCallback() {
        this.bindings = await initializeBindings(this);
        this.unsubscribe = [];
        this.breadcrumbManager = buildBreadcrumbManager(this.bindings.engine);
        this.querySummary = buildQuerySummary(this.bindings.engine);

        this.cacheDom();
        if (!this.hasRequiredDom()) {
            console.warn("custom-facet-filters requires inline markup in page HTML");
            return;
        }
        this.bindStaticEvents();

        this.mobileQuery = window.matchMedia("(max-width: 767.5px)");
        this.handleViewportChange = () => this.syncForViewport();
        this.mobileQuery.addEventListener("change", this.handleViewportChange);
        this.handleEscape = (event) => {
            if (event.key === "Escape") {
                this.closeModal();
            }
        };
        document.addEventListener("keydown", this.handleEscape);
        this.syncForViewport();
    }

    disconnectedCallback() {
        this.closeModal();
        this.restoreFacetsToDesktop();
        this.teardownSubscriptions();
        if (this.mobileQuery && this.handleViewportChange) {
            this.mobileQuery.removeEventListener("change", this.handleViewportChange);
        }
        document.removeEventListener("keydown", this.handleEscape);
    }

    syncForViewport() {
        const shouldBeMobile = this.mobileQuery.matches;
        if (this.isMobileView === shouldBeMobile) {
            return;
        }

        this.isMobileView = shouldBeMobile;

        if (this.isMobileView) {
            this.toggleWrap.classList.remove("hidden");
            this.moveFacetsToModal();
            this.subscribeControllers();
            this.updateSelectedCount();
            this.updateApplyLabel();
            return;
        }

        this.toggleWrap.classList.add("hidden");
        this.closeModal();
        this.teardownSubscriptions();
        this.restoreFacetsToDesktop();
    }

    // The desktop atomic-facet-manager is relocated into the mobile modal so both breakpoints share the same Atomic facets.
    moveFacetsToModal() {
        const facetManager = this.getDesktopFacetManager();
        if (!facetManager || facetManager.parentElement === this.facetsContainer) {
            return;
        }

        if (!this.facetManagerPlaceholder) {
            this.facetManagerPlaceholder = document.createComment("atomic-facet-manager placeholder");
        }
        facetManager.parentElement.insertBefore(this.facetManagerPlaceholder, facetManager);
        this.facetsContainer.append(facetManager);
    }

    restoreFacetsToDesktop() {
        const placeholder = this.facetManagerPlaceholder;
        const facetManager = this.facetsContainer?.querySelector("atomic-facet-manager");
        if (!placeholder?.parentNode || !facetManager) {
            return;
        }

        placeholder.parentNode.insertBefore(facetManager, placeholder);
        placeholder.remove();
    }

    getDesktopFacetManager() {
        if (!this.desktopFacetManager?.isConnected) {
            this.desktopFacetManager = document.querySelector("atomic-layout-section[section='facets'] atomic-facet-manager");
        }
        return this.desktopFacetManager;
    }

    subscribeControllers() {
        this.teardownSubscriptions();
        this.unsubscribe = [
            this.breadcrumbManager.subscribe(() => this.updateSelectedCount()),
            this.querySummary.subscribe(() => this.updateApplyLabel())
        ];
    }

    teardownSubscriptions() {
        (this.unsubscribe || []).forEach((cancel) => cancel());
        this.unsubscribe = [];
    }

    hasRequiredDom() {
        return Boolean(
            this.toggleWrap &&
            this.openFiltersButton &&
            this.selectedCount &&
            this.modalRoot &&
            this.resetFiltersButton &&
            this.applyFiltersButton &&
            this.facetsContainer
        );
    }

    cacheDom() {
        this.toggleWrap = this.querySelector("#toggleWrap");
        this.openFiltersButton = this.querySelector("#openFilters");
        this.selectedCount = this.querySelector("#selectedCount");
        this.modalRoot = this.querySelector("#modalRoot");
        this.resetFiltersButton = this.querySelector("#resetFilters");
        this.applyFiltersButton = this.querySelector("#applyFilters");
        this.facetsContainer = this.querySelector("#facetsContainer");
    }

    bindStaticEvents() {
        this.openFiltersButton.addEventListener("click", () => this.openModal());
        this.resetFiltersButton.addEventListener("click", () => this.resetAllFilters());
        this.applyFiltersButton.addEventListener("click", () => this.closeModal());
        this.modalRoot.addEventListener("click", (event) => {
            const closeButton = event.target.closest("[data-close-modal]");
            if (closeButton) {
                this.closeModal();
            }
        });
    }

    openModal() {
        if (!this.isMobileView) {
            return;
        }

        this.modalRoot.classList.remove("hidden");
        this.modalRoot.setAttribute("aria-hidden", "false");
        this.openFiltersButton.setAttribute("aria-expanded", "true");
        if (!this.previousBodyOverflow) {
            this.previousBodyOverflow = document.body.style.overflow || "";
        }
        document.body.style.overflow = "hidden";

        requestAnimationFrame(() => {
            this.modalRoot.querySelector(".close-modal")?.focus();
        });
    }

    closeModal() {
        if (!this.modalRoot || this.modalRoot.classList.contains("hidden")) {
            return;
        }

        this.modalRoot.classList.add("hidden");
        this.modalRoot.setAttribute("aria-hidden", "true");
        if (this.openFiltersButton) {
            this.openFiltersButton.setAttribute("aria-expanded", "false");
        }
        document.body.style.overflow = this.previousBodyOverflow || "";
        this.previousBodyOverflow = "";
    }

    resetAllFilters() {
        this.breadcrumbManager.deselectAll();
    }

    updateSelectedCount() {
        if (!this.selectedCount || !this.breadcrumbManager) {
            return;
        }

        const state = this.breadcrumbManager.state;
        const breadcrumbGroups = [
            state.facetBreadcrumbs,
            state.categoryFacetBreadcrumbs,
            state.numericFacetBreadcrumbs,
            state.dateFacetBreadcrumbs,
            state.staticFilterBreadcrumbs
        ];

        const selected = breadcrumbGroups.reduce(
            (total, group) => total + (group || []).reduce((sum, breadcrumb) => sum + (breadcrumb.values || []).length, 0),
            0
        );

        this.selectedCount.textContent = selected > 0 ? `(${selected})` : "";
    }

    updateApplyLabel() {
        if (!this.applyFiltersButton || !this.querySummary) {
            return;
        }

        const total = Number(this.querySummary.state.total ?? 0);
        this.applyFiltersButton.textContent = `Show ${total} results`;
    }
}

if (!window.customElements.get("custom-facet-filters")) {
    window.customElements.define("custom-facet-filters", CustomFacetFilters);
}